import Vue from "vue";
import Router, { NavigationGuardNext, Route } from "vue-router";

import RootTemplate from "@/components/templates/RootTemplate.vue";
import PlainTemplate from "@/components/templates/PlainTemplate.vue";
import NovelTemplate from "@/components/templates/NovelTemplate.vue";
import HeaderTemplate from "@/components/templates/HeaderTemplate.vue";

import { Auth } from "aws-amplify";

// import { openNotifyDialog } from "@/lib/dialog";
import { Plugin } from "@/lib/modified-status";
import { callApi } from "@/lib/api";
import { isMaintainance } from "@/lib/isMaintainance";
import isMobile from "ismobilejs";
import { usePageEditStore } from "@/stores/pageEdit";
import i18n from "@/lib/i18n";

/* Home */
const Home = () => import(/* webpackChunkName: "group-mypage" */ "./pages/HomePage.vue");
const News = () => import(/* webpackChunkName: "group-mypage" */ "./pages/News.vue");
const NolaNovel = () => import(/* webpackChunkName: "group-mypage" */ "./pages/NolaNovel.vue");
const EventTheme = () => import(/* webpackChunkName: "group-mypage" */ "./pages/EventTheme.vue");
const EventContest = () => import(/* webpackChunkName: "group-mypage" */ "./pages/EventContest.vue");
const EventContestDetail = () => import(/* webpackChunkName: "group-mypage" */ "./pages/EventContestDetail.vue");
const EventContestEntry = () => import(/* webpackChunkName: "group-mypage" */ "./pages/EventContestEntry.vue");
const EventAgentEntry = () => import(/* webpackChunkName: "group-mypage" */ "./pages/EventAgentEntry.vue");
const EventCompany = () => import(/* webpackChunkName: "group-mypage" */ "./pages/EventCompany.vue");
const EnteredContests = () => import(/* webpackChunkName: "group-mypage" */ "./pages/EnteredContests.vue");
const EntryProfile = () => import(/* webpackChunkName: "group-mypage" */ "./pages/EntryProfile.vue");
const MyPage = () => import(/* webpackChunkName: "group-mypage" */ "./pages/MyPage.vue");
const PurchaseFish = () => import(/* webpackChunkName: "group-mypage" */ "./pages/PurchaseFish.vue");
const FishHistory = () => import(/* webpackChunkName: "group-mypage" */ "./pages/FishHistory.vue");
const NovelCreate = () => import(/* webpackChunkName: "group-mypage" */ "./pages/novel/NovelCreatePage.vue");
const Agent = () => import("./pages/Agent.vue");
const AgentIntro = () => import("./pages/AgentIntro.vue");
const AgentDescription = () => import("./pages/AgentDescription.vue");
const CommerceDebutGuide = () => import("./pages/CommerceDebutGuide.vue");
const Inbox = () => import("./pages/Inbox.vue");

/* Template */
const header = () => import(/* webpackChunkName: "group-mypage" */ "./components/organisms/Header.vue");
const headerOnlyLogo = () => import("./components/organisms/HeaderOnlyLogo.vue");
const side = () => import(/* webpackChunkName: "group-mypage" */ "./components/organisms/SideMenu.vue");
const fixedSide = () => import("./components/organisms/FixedSideMenu.vue");
const nolaNovelSideMenu = () => import("./components/organisms/NolaNovelSideMenu.vue");
const eventSideMenu = () => import("./components/organisms/EventSideMenu.vue");
const agentSideMenu = () => import("./components/organisms/AgentSideMenu.vue");
const commerceDebutGuideSideMenu = () => import("./components/organisms/CommerceDebutGuideSideMenu.vue");

/* TopPage */
const Top = () => import(/* webpackChunkName: "group-mypage" */ "./pages/TopPage.vue");
const ChangePassword = () => import(/* webpackChunkName: "group-mypage" */ "./pages/ChangePasswordPage.vue");
const ChangeEmail = () => import(/* webpackChunkName: "group-mypage" */ "./pages/ChangeEmailPage.vue");

/* Auth */
const SignUp = () => import(/* webpackChunkName: "group-auth" */ "./pages/auth/SignUpPage.vue");
const ResendConfirmCode = () => import(/* webpackChunkName: "group-auth" */ "./pages/auth/ResendConfirmCodePage.vue");
const SignIn = () => import(/* webpackChunkName: "group-auth" */ "./pages/auth/SignInPage.vue");
const SignInConnectedAccount = () =>
  import(/* webpackChunkName: "group-auth" */ "./pages/auth/SignInConnectedAccountPage.vue");
const ResetPassword = () => import(/* webpackChunkName: "group-auth" */ "./pages/auth/ResetPasswordPage.vue");
const SchoolAccountResetPassword = () =>
  import(/* webpackChunkName: "group-auth" */ "./pages/auth/SchoolAccountResetPasswordPage.vue");
const ResetPasswordSubmit = () =>
  import(/* webpackChunkName: "group-auth" */ "./pages/auth/ResetPasswordSubmitPage.vue");
const NewPassword = () => import(/* webpackChunkName: "group-auth" */ "./pages/auth/NewPasswordPage.vue");
const ConfirmEmail = () => import(/* webpackChunkName: "group-auth" */ "./pages/auth/ConfirmEmailPage.vue");
const AccountDeleted = () => import(/* webpackChunkName: "group-mypage" */ "./pages/statics/AccountDeleted.vue");

/* Novel */
const NovelDetail = () => import(/* webpackChunkName: "group-novel" */ "./pages/novel/NovelDetailPage.vue");
const NovelDetailEdit = () => import(/* webpackChunkName: "group-novel" */ "./pages/novel/NovelDetailEditPage.vue");

const Characters = () => import(/* webpackChunkName: "group-novel" */ "./pages/novel/CharactersPage.vue");
const CharacterCreate = () => import(/* webpackChunkName: "group-novel" */ "./pages/novel/CharacterCreatePage.vue");
const CharacterEdit = () => import(/* webpackChunkName: "group-novel" */ "./pages/novel/CharacterEditPage.vue");

const WorldViews = () => import(/* webpackChunkName: "group-novel" */ "./pages/novel/WorldViewsPage.vue");
const WorldViewCreate = () => import(/* webpackChunkName: "group-novel" */ "./pages/novel/WorldViewCreatePage.vue");
const WorldViewEdit = () => import(/* webpackChunkName: "group-novel" */ "./pages/novel/WorldViewEditPage.vue");

const Correlations = () => import(/* webpackChunkName: "group-novel" */ "./pages/novel/CorrelationsPage.vue");

const Plot = () => import(/* webpackChunkName: "group-novel" */ "./pages/novel/PlotPage.vue");
const PlotEdit = () => import(/* webpackChunkName: "group-novel" */ "./pages/novel/PlotEditPage.vue");
const PlotChange = () => import(/* webpackChunkName: "group-novel" */ "./pages/novel/PlotChangePage.vue");

const PlotMaker = () => import(/* webpackChunkName: "group-novel" */ "./pages/novel/PlotMakerPage.vue");

const Timeline = () => import("./pages/novel/TimelinePage.vue");

const Theme = () => import(/* webpackChunkName: "group-novel" */ "./pages/novel/ThemePage.vue");
const ManuscriptEditor = () => import(/* webpackChunkName: "group-novel" */ "./pages/novel/ManuscriptEditorPage.vue");
const ManuscriptPreview = () => import(/* webpackChunkName: "group-novel" */ "./pages/novel/ManuscriptPreviewPage.vue");
const MemoInNovel = () => import("./pages/novel/Memo.vue");
const SharedContent = () => import("./pages/novel/SharedContentPage.vue");
const MultiManuscriptEditor = () => import("./pages/novel/MultiManuscriptEditorPage.vue");

const Materials = () => import("./pages/novel/MaterialsPage.vue");
const MaterialTemplates = () => import("./pages/novel/MaterialTemplatesPage.vue");
const MaterialEdit = () => import("./pages/novel/MaterialEditPage.vue");
const MaterialTemplateEdit = () => import("./pages/novel/MaterialTemplateEditPage.vue");
const VerticalExport = () => import(/* webpackChunkName: "group-export" */ "./pages/export/VerticalExportPage.vue");
const HorizontalExport = () => import(/* webpackChunkName: "group-export" */ "./pages/export/HorizontalExportPage.vue");

const Terms = () => import(/* webpackChunkName: "group-static" */ "./pages/statics/Terms.vue");
const SchoolAccountTerms = () =>
  import(/* webpackChunkName: "group-static" */ "./pages/statics/SchoolAccountTerms.vue");
const Privacy = () => import(/* webpackChunkName: "group-static" */ "./pages/statics/Privacy.vue");
const Membership = () => import(/* webpackChunkName: "group-static" */ "./pages/statics/Membership.vue");
const Shared = () => import(/* webpackChunkName: "group-static" */ "./pages/statics/Shared.vue");
const Contact = () => import(/* webpackChunkName: "group-static" */ "./pages/statics/Contact.vue");
const SpecifiedCommercialTransactionsAct = () =>
  import(/* webpackChunkName: "group-static" */ "./pages/statics/SpecifiedCommercialTransactionsAct.vue");
const PaymentServicesAct = () =>
  import(/* webpackChunkName: "group-static" */ "./pages/statics/PaymentServicesAct.vue");

/** Payment */
const SubscriptionAnnounce = () => import("./pages/statics/SubscriptionAnnounce.vue");
const PaymentManagement = () =>
  import(/* webpackChunkName: "group-static" */ "./pages/payment/PaymentManagementPage.vue");
const SubscriptionCompleted = () =>
  import(/* webpackChunkName: "group-static" */ "./pages/statics/SubscriptionCompleted.vue");
const UnsubscriptionAnnounce = () =>
  import(/* webpackChunkName: "group-static" */ "./pages/statics/UnsubscriptionAnnounce.vue");
const ConfirmInAppPurchase = () =>
  import(/* webpackChunkName: "group-static" */ "./pages/statics/ConfirmInAppPurchase.vue");

/** メモ画面(単体) */
const Memo = () => import("./pages/Memo.vue");

const ErrorNotFoundPage = () => import(/* webpackChunkName: "group-static" */ "./pages/error/NotFoundPage.vue");
const ErrorMaintenancePage = () => import(/* webpackChunkName: "group-static" */ "./pages/error/MaintenancePage.vue");
const SharedLinkAccessDenied = () => import("./pages/error/SharedLinkAccessDeniedPage.vue");
const AnnualReport2023 = () => import(/* webpackChunkName: "group-static" */ "./pages/error/AnnualReport2023.vue");

/** フィルムアート社 特設ページ */
const Knowhow = () => import(/* webpackChunkName: "group-static" */ "./pages/statics/Knowhow.vue");

Vue.use(Router);

/** 認証タイプ */
const enum AuthType {
  /** 認証済ユーザーのみ表示 */
  REQUIRE_AUTH = "require_auth",
  /** 未認証ユーザーのみ表示 */
  REQUIRE_NOT_AUTH = "require_not_auth",
}

interface PageParam {
  path: string;
  redirect?: string;
  name?: string;
  component?: any;
  template?: any;
  headerTemplate?: any;
  sideTemplate?: any;
  title?: string;
  authType?: string | null;
}

function RootPage({
  path,
  name,
  component,
  template,
  title,
  redirect,
  headerTemplate,
  sideTemplate,
  authType = AuthType.REQUIRE_AUTH,
}: PageParam) {
  return {
    path,
    redirect,
    name,
    components: {
      template: template || RootTemplate,
      default: component,
      header: headerTemplate || header,
      side: sideTemplate || side,
    },
    props: { template: true, default: true, side: true },
    meta: {
      authType,
      title,
    },
  };
}

function NovelPage({
  path,
  name,
  component,
  template,
  title,
  headerTemplate,
  sideTemplate,
  authType = AuthType.REQUIRE_AUTH,
}: PageParam) {
  return {
    path,
    name,
    components: {
      template: template || NovelTemplate,
      default: component,
      header: isMobileDevice() ? header : headerTemplate,
      side: sideTemplate || side,
    },
    props: { template: true, default: true, side: true },
    meta: {
      authType,
      title,
    },
    beforeEnter: async (to: Route, _: Route, next: NavigationGuardNext) => {
      if (Object.prototype.hasOwnProperty.call(to.params, "novelId")) {
        const result = await existsNovel(to.params.novelId);
        if (result) {
          next();
        } else {
          next({ name: "home" });
        }
      } else {
        next();
      }
    },
  };
}

function PlainPage({ path, name, component, title, headerTemplate }: PageParam, authType: AuthType | null = null) {
  return {
    path,
    name,
    components: {
      template: PlainTemplate,
      default: component,
      header: headerTemplate,
    },
    props: { default: true },
    meta: {
      authType,
      title,
    },
  };
}

const router = new Router({
  mode: "history",
  base: "",
  routes: [
    {
      path: "/index.html",
      redirect: "/home",
    },
    PlainPage(
      {
        path: "/",
        name: "top",
        component: Top,
      },
      AuthType.REQUIRE_NOT_AUTH
    ),
    PlainPage(
      {
        path: "/lp",
        name: "top-lp",
        component: Top,
      },
      AuthType.REQUIRE_NOT_AUTH
    ),
    RootPage({
      template: HeaderTemplate,
      path: "/home",
      name: "home",
      component: Home,
    }),
    RootPage({
      template: HeaderTemplate,
      path: "/news",
      name: "news",
      component: News,
    }),
    RootPage({
      template: HeaderTemplate,
      path: "/inbox",
      redirect: "/inbox/nola",
    }),
    RootPage({
      title: "inboxPage.pageTitle",
      template: HeaderTemplate,
      path: "/inbox/:tab",
      name: "inbox",
      component: Inbox,
    }),
    RootPage({
      path: "/event",
      redirect: "/event/theme",
    }),
    RootPage({
      path: "/event/theme",
      name: "eventTheme",
      component: EventTheme,
      sideTemplate: eventSideMenu,
    }),
    RootPage({
      path: "/event/contest",
      name: "eventContest",
      component: EventContest,
      sideTemplate: eventSideMenu,
    }),
    RootPage({
      path: "/event/contest/:id",
      name: "eventContestDetail",
      component: EventContestDetail,
      sideTemplate: eventSideMenu,
    }),
    RootPage({
      path: "/event/contest/:id/entry",
      redirect: "/event/contest/:id",
    }),
    RootPage({
      path: "/event/contest/:id/entry/:contestId",
      name: "eventContestEntry",
      component: EventContestEntry,
      sideTemplate: eventSideMenu,
    }),
    RootPage({
      path: "/event/contest/:id/entry/agent/:campaignId",
      name: "eventAgentEntry",
      component: EventAgentEntry,
      sideTemplate: eventSideMenu,
    }),
    RootPage({
      path: "/event/company",
      name: "eventCompany",
      component: EventCompany,
      sideTemplate: eventSideMenu,
    }),
    RootPage({
      path: "/entries",
      name: "entries",
      component: EnteredContests,
      sideTemplate: eventSideMenu,
    }),
    RootPage({
      path: "/entryProfile",
      name: "entryProfile",
      component: EntryProfile,
      sideTemplate: eventSideMenu,
    }),
    RootPage({
      path: "/nolanovel",
      name: "nolaNovel",
      component: NolaNovel,
      sideTemplate: nolaNovelSideMenu,
    }),
    RootPage({
      title: "myPage.pageTitle",
      template: HeaderTemplate,
      path: "/mypage",
      name: "myPage",
      component: MyPage,
    }),
    RootPage({
      title: "paymentManagement.pageTitle",
      template: HeaderTemplate,
      path: "/payment-management",
      name: "paymentManagement",
      component: PaymentManagement,
    }),
    RootPage({
      title: "purchaseFish.pageTitle",
      template: HeaderTemplate,
      path: "/purchase-fish",
      name: "purchaseFish",
      component: PurchaseFish,
    }),
    RootPage({
      title: "fishHistory.pageTitle",
      template: HeaderTemplate,
      path: "/fish-history",
      name: "fishHistory",
      component: FishHistory,
    }),
    RootPage({
      title: "changePasswordPage.pageTitle",
      template: HeaderTemplate,
      path: "/changepassword",
      name: "changePassword",
      component: ChangePassword,
    }),
    RootPage({
      title: "changeEmailPage.pageTitle",
      template: HeaderTemplate,
      path: "/changeemail",
      name: "changeEmail",
      component: ChangeEmail,
    }),
    RootPage({
      title: "novelCreatePage.pageTitle",
      template: HeaderTemplate,
      path: "/novels/create",
      name: "novelCreate",
      component: NovelCreate,
    }),
    RootPage({
      title: "agentPage.pageTitle",
      path: "/agent",
      name: "agent",
      component: Agent,
      sideTemplate: agentSideMenu,
    }),
    RootPage({
      title: "agentDescriptionPage.pageTitle",
      path: "/agent/description",
      name: "agentDescription",
      component: AgentDescription,
      sideTemplate: agentSideMenu,
    }),
    RootPage({
      title: "agentIntro.pageTitle",
      path: "/agent/intro",
      name: "agentIntro",
      component: AgentIntro,
      template: HeaderTemplate,
    }),
    RootPage({
      title: "commerceDebutGuidePage.pageTitle",
      path: "/commerce-debut-guide",
      name: "commerceDebutGuide",
      component: CommerceDebutGuide,
      sideTemplate: commerceDebutGuideSideMenu,
    }),
    NovelPage({
      title: "novelDetail.pageTitle",
      name: "detail",
      path: "/:novelId/detail",
      component: NovelDetail,
    }),
    NovelPage({
      title: "novelDetailEditPage.pageTitle",
      name: "detailEdit",
      path: "/:novelId/detail/edit",
      component: NovelDetailEdit,
    }),
    NovelPage({
      title: "themePage.pageTitle",
      name: "theme",
      path: "/:novelId/theme",
      component: Theme,
    }),
    NovelPage({
      title: "createCharacterPage.pageTitle",
      name: "characterCreate",
      path: "/:novelId/characters/create",
      component: CharacterCreate,
    }),
    NovelPage({
      title: "characterEditPage.pageTitle",
      name: "characterEdit",
      path: "/:novelId/characters/:characterId?/edit",
      component: CharacterEdit,
    }),
    NovelPage({
      title: "characterPage.pageTitle",
      name: "characters",
      path: "/:novelId/characters/:characterId?",
      component: Characters,
    }),
    NovelPage({
      title: "createWorldViewPage.pageTitle",
      name: "worldViewCreate",
      path: "/:novelId/worldviews/create",
      component: WorldViewCreate,
    }),
    NovelPage({
      title: "worldViewEditPage.pageTitle",
      name: "worldViewEdit",
      path: "/:novelId/worldviews/:worldViewId/edit",
      component: WorldViewEdit,
    }),
    NovelPage({
      title: "worldViewPage.pageTitle",
      name: "worldViews",
      path: "/:novelId/worldviews/:worldViewId?",
      component: WorldViews,
    }),
    NovelPage({
      title: "correlationsPage.pageTitle",
      name: "correlations",
      path: "/:novelId/correlations/:correlationGroupId?/:correlationId?",
      component: Correlations,
    }),
    NovelPage({
      title: "materialPage.pageTitle",
      name: "materials",
      path: "/:novelId/materials/:materialAttributeId?/:materialId?",
      component: Materials,
    }),
    NovelPage({
      title: "materialEditPage.pageTitle",
      name: "materialEdit",
      path: "/:novelId/materials/:materialAttributeId/:materialId/edit",
      component: MaterialEdit,
    }),
    NovelPage({
      title: "materialTemplatesPage.pageTitle",
      name: "templates",
      path: "/:novelId/templates/:templateId?",
      component: MaterialTemplates,
    }),
    NovelPage({
      title: "materialTemplateEditPage.pageTitle",
      name: "templateEdit",
      path: "/:novelId/templates/:templateId/edit",
      component: MaterialTemplateEdit,
    }),
    NovelPage({
      title: "manuscriptPage.pageTitle",
      name: "editor",
      path: "/:novelId/editor/:manuscriptKey?",
      component: ManuscriptEditor,
    }),
    PlainPage(
      {
        title: "multiManuscriptEditorPage.pageTitle",
        name: "multiEditor",
        path: "/:novelId/multiEditor/:manuscriptKey?",
        component: MultiManuscriptEditor,
      },
      AuthType.REQUIRE_AUTH
    ),
    PlainPage(
      {
        title: "manuscriptPreviewPage.pageTitle",
        name: "preview",
        path: "/:novelId/preview",
        component: ManuscriptPreview,
      },
      AuthType.REQUIRE_AUTH
    ),
    NovelPage({
      title: "plotPage.pageTitle",
      name: "plot",
      path: "/:novelId/plot/:plotId?",
      component: Plot,
    }),
    NovelPage({
      title: "プロットメーカー",
      name: "plotMaker",
      path: "/:novelId/plot-maker/:plotMakerId?",
      component: PlotMaker,
    }),
    NovelPage({
      title: "plotEditPage.pageTitle",
      name: "plotEdit",
      path: "/:novelId/plot/:plotId?/edit",
      component: PlotEdit,
    }),
    NovelPage({
      title: "plotChangePage.pageTitle",
      name: "plotChange",
      path: "/:novelId/plot/:plotId?/change",
      component: PlotChange,
    }),
    NovelPage({
      title: "timelinePage.pageTitle",
      name: "timeline",
      path: "/:novelId/timeline/:timelineType?/:timelineId?",
      component: Timeline,
    }),
    PlainPage(
      {
        path: "/auth/signup",
        name: "signUp",
        component: SignUp,
      },
      AuthType.REQUIRE_NOT_AUTH
    ),
    PlainPage(
      {
        path: "/auth/signin",
        name: "signIn",
        component: SignIn,
      },
      AuthType.REQUIRE_NOT_AUTH
    ),
    PlainPage(
      {
        path: "/auth/signinConnectedAccount",
        name: "signInConnectedAccount",
        component: SignInConnectedAccount,
      },
      AuthType.REQUIRE_NOT_AUTH
    ),
    PlainPage(
      {
        path: "/auth/resendconfirmcode",
        name: "resendConfirmCode",
        component: ResendConfirmCode,
      },
      AuthType.REQUIRE_NOT_AUTH
    ),
    PlainPage(
      {
        path: "/auth/resetpassword",
        name: "resetPassword",
        component: ResetPassword,
      },
      AuthType.REQUIRE_NOT_AUTH
    ),
    PlainPage(
      {
        path: "/auth/schoolaccount-resetpassword",
        name: "schoolAccountResetPassword",
        component: SchoolAccountResetPassword,
      },
      AuthType.REQUIRE_NOT_AUTH
    ),
    PlainPage(
      {
        path: "/auth/resetpasswordsubmit/:username",
        name: "resetPasswordSubmit",
        component: ResetPasswordSubmit,
      },
      AuthType.REQUIRE_NOT_AUTH
    ),
    PlainPage(
      {
        path: "/auth/newpassword",
        name: "newPassword",
        component: NewPassword,
      },
      AuthType.REQUIRE_NOT_AUTH
    ),
    PlainPage({
      path: "/auth/confirmemail/:id",
      name: "confirmEmail",
      component: ConfirmEmail,
    }),
    PlainPage(
      {
        title: "accountDeletedPage.pageTitle",
        path: "/accountDeleted",
        name: "accountDeleted",
        component: AccountDeleted,
        headerTemplate: headerOnlyLogo,
      },
      AuthType.REQUIRE_NOT_AUTH
    ),
    PlainPage({
      path: "/privacy",
      name: "privacy",
      component: Privacy,
    }),
    PlainPage({
      path: "/terms",
      name: "terms",
      component: Terms,
    }),
    PlainPage({
      path: "/schoolAccountTerms",
      name: "schoolAccountTerms",
      component: SchoolAccountTerms,
    }),
    PlainPage({
      path: "/membership",
      name: "membership",
      component: Membership,
    }),
    PlainPage({
      path: "/shared",
      name: "shared",
      component: Shared,
    }),
    PlainPage({
      path: "/specifiedCommercialTransactionsAct",
      name: "specifiedCommercialTransactionsAct",
      component: SpecifiedCommercialTransactionsAct,
    }),
    PlainPage({
      path: "/paymentServicesAct",
      name: "paymentServicesAct",
      component: PaymentServicesAct,
    }),
    PlainPage({
      path: "/maintenance",
      name: "maintenance",
      component: ErrorMaintenancePage,
    }),
    PlainPage(
      {
        path: "/subscription",
        name: "subscriptionAnnounce",
        component: SubscriptionAnnounce,
      },
      AuthType.REQUIRE_AUTH
    ),
    RootPage({
      template: HeaderTemplate,
      path: "/subscription/completed",
      name: "subscriptionCompleted",
      component: SubscriptionCompleted,
    }),
    RootPage({
      template: HeaderTemplate,
      path: "/unsubscription",
      name: "unsubscriptionAnnounce",
      component: UnsubscriptionAnnounce,
    }),
    RootPage({
      template: HeaderTemplate,
      path: "/confirmInAppPurchase",
      name: "confirmInAppPurchase",
      component: ConfirmInAppPurchase,
    }),
    RootPage({
      template: HeaderTemplate,
      path: "/unsubscription/completed",
      name: "unsubscriptionCompleted",
      component: SubscriptionCompleted,
    }),
    RootPage({
      template: HeaderTemplate,
      path: "/contact",
      name: "contact",
      component: Contact,
      authType: null,
    }),
    NovelPage({
      path: "/memo/:memoId?",
      name: "memo",
      component: Memo,
      sideTemplate: fixedSide,
    }),
    NovelPage({
      path: "/:novelId/memo/:memoId?",
      name: "memoInNovel",
      component: MemoInNovel,
    }),
    PlainPage(
      {
        path: "/share/:sharedLinkId/:novelId/:contentKey?",
        name: "sharedContent",
        component: SharedContent,
        headerTemplate: headerOnlyLogo,
      },
      AuthType.REQUIRE_AUTH
    ),
    PlainPage({
      path: "/share/accessdenied",
      name: "sharedLinkAccessDenied",
      component: SharedLinkAccessDenied,
      headerTemplate: headerOnlyLogo,
    }),
    PlainPage({
      title: "縦書きPDF出力",
      name: "verticalExport",
      path: "/verticalExport",
      component: VerticalExport,
    }),
    PlainPage({
      title: "横書きPDF出力",
      name: "horizontalExport",
      path: "/horizontalExport",
      component: HorizontalExport,
    }),
    PlainPage({
      title: "knowhowPage.pageTitle",
      path: "/knowhow",
      name: "knowhow",
      component: Knowhow,
    }),
    PlainPage({
      title: "annualReport2023Page.pageTitle",
      path: "/annual-report-2023",
      name: "annualReport2023",
      component: AnnualReport2023,
    }),
    // Debug関連のルートを動的に追加

    {
      path: "*",
      name: "404",
      components: {
        template: PlainTemplate,
        default: ErrorNotFoundPage,
      },
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        selector: to.hash,
      };
    }

    return { x: 0, y: 0 };
  },
});

Vue.use(new Plugin());
// router.beforeEach(async (to, from, next) => {
//   // 保存
//   if (isModified()) {
//     const yes = await openNotifyDialog({ content: '保存されていない変更があるけど戻る？' });
//     if (!yes) {
//       return;
//     }
//     setModified(false);
//   }
//   next();
// });

function postMessage(type: unknown, message: any = null) {
  if (window.parent !== window.top) {
    window.parent.postMessage({ nola: true, type, message }, "*");
  }
}

router.beforeEach((to, from, next) => {
  let { title } = to.meta;
  if (title) {
    // i18nキーかどうかチェック（ドットを含む場合はi18nキーと判断）
    if (typeof title === "string" && title.includes(".")) {
      title = `Nola ${i18n.t(title)}`;
    } else {
      title = `Nola ${title}`;
    }
  } else {
    // titleが設定されていない場合は環境に応じたデフォルトタイトルを設定
    const isProduction = process.env.NODE_ENV === "production";
    title = i18n.t(isProduction ? "app.defaultTitle" : "app.defaultTitleDev") as string;
  }
  document.title = title;
  next();
});

router.beforeEach(async (to, from, next) => {
  // メンテナンス時にメンテナンス画面にリダイレクト
  const _isMaintenance = await isMaintainance();
  if (_isMaintenance && to.name !== "maintenance") {
    next({ name: "maintenance" });
    return;
  }
  if (!_isMaintenance && to.name === "maintenance") {
    next({ name: "top" });
    return;
  }

  postMessage("history", to.fullPath);

  const user = (await Auth.currentCredentials().catch((err) => {
    if (err.code === "UserNotFoundException" || err.code === "NotAuthorizedException") {
      return;
    }
    if (err !== "No current user") {
      throw err;
    }
  })) as any;

  const isAuthenticated = user.authenticated;
  const isLogin = user && !user.code && isAuthenticated;

  if (isLogin) {
    try {
      const sub = user.signInUserSession ? user.signInUserSession.idToken.payload.sub : user.data.IdentityId;

      (Vue as any).$ga.set("userId", sub);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }

  /** 未ログインユーザーが共有リンクにアクセスした時、新規登録案内へリダイレクト */
  if (to.name === "sharedContent") {
    if (!isLogin) {
      next({
        name: "sharedLinkAccessDenied",
        query: { reason: "notlogin" },
      });
      return;
    }
  }

  if (to.matched.some((record) => record.meta.authType === AuthType.REQUIRE_AUTH)) {
    if (!isLogin) {
      next({
        name: "top",
        query: { redirect: to.fullPath },
      });
      return;
    }
  } else if (to.matched.some((record) => record.meta.authType === AuthType.REQUIRE_NOT_AUTH)) {
    if (isLogin) {
      next({
        name: "home",
      });
      return;
    }
  }
  next();
});

router.beforeEach(async (to, from, next) => {
  // 編集を保存していない状態で画面遷移をしようとした際に確認ダイアログを表示する
  const pageEditStore = usePageEditStore();
  if (await pageEditStore.confirmUnsavedChanges()) {
    next(); // ユーザーが遷移を許可した場合
  } else {
    next(false); // ユーザーが遷移をキャンセルした場合
  }
});

async function existsNovel(novelId: string) {
  const { novel } = await callApi(
    `query Novel ($novelId: ID!) {
        novel ( novelId: $novelId ) {
          novelId
        }
      }`,
    {
      novelId,
    }
  );

  if (novel) {
    return true;
  }
  return false;
}

function isMobileDevice() {
  return isMobile().phone || (isMobile().tablet && window.innerWidth < 1024);
}

export default router;
